import {ButtonWithIcon, LabelWithoutJson} from '@/components/atoms'
import {AccordionItemCustom as AccordionItem} from '@/components/molecules/AccordionItemCustom'
import {useSegmentsList} from '@/hooks/useSegmentsList'
import {PromotionStep} from '@/types/promotion'
import {getSegmentsDatafromSegmentsIds} from '@/utils/commons'
import {COLOR, COLOR_CODE} from '@/utils/constants/colors'
import {FONT_SIZE, FONT_WEIGHT, SIZE} from '@/utils/constants/globalStyle'

const PromotionsReviewSetup = ({promotionReviewData, isDetailView, handleEditStep}) => {
  const LabelWithVal = ({keyName, val}) => {
    const id = keyName?.toLowerCase()?.replaceAll(' ', '_') ?? ''
    return (
      <div className="col-4 w-full overflow-hidden pt-5">
        <div>
          <LabelWithoutJson
            id={`${id}_label`}
            text={keyName}
            fontWeight={FONT_WEIGHT.NORMAL}
            fontSize={FONT_SIZE.XS}
            className="font-body"
          />
          <LabelWithoutJson
            id={`${id}_value`}
            text={val}
            isValueDiasbledInput={false}
            fontWeight={FONT_WEIGHT.BOLD}
            fontSize={FONT_SIZE.SM}
            className="font-body"
            color={COLOR_CODE._FFFFFF}
          />
        </div>
      </div>
    )
  }

  const {segmentsGroups} = useSegmentsList()

  const getExcludedItemsDisplay = () => {
    let excludedItems = promotionReviewData?.excludedItems

    if (!Array.isArray(excludedItems) || excludedItems.length === 0) {
      return '-'
    }

    const excludedItem = excludedItems?.[0] || {items: [], itemType: ''}

    if (!excludedItem?.items || excludedItem.items.length === 0) {
      return '-'
    }

    return excludedItem.items.map(item => item.name || item.id).join(', ')
  }

  const getProductCombination = () => {
    const firstCondition = promotionReviewData?.promotionConditions?.[0]?.conditionGroup?.conditions?.[0]
    return firstCondition?.productCombination ? 'Yes' : 'No'
  }

  const getAllCustomersValue = segmentType => {
    return segmentType === 'ALL_CUSTOMERS' ? 'Yes' : 'No'
  }

  const renderSegmentContent = () => {
    const segmentType =
      promotionReviewData?.segments?.type || promotionReviewData?.segments?.selectedSegments?.[0]?.selectionType

    // Quick helper to safely stringify arrays
    const stringifyArray = arr => (Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '-')

    // Get excluded segments (handles multiple possible shapes)
    const exludedSegments = getSegmentsDatafromSegmentsIds(
      promotionReviewData?.segments?.excludedSegments,
      segmentsGroups
    )

    const getExcludedSegmentsDisplay = () => {
      const excludedData = promotionReviewData?.segments?.excludedSegments

      // Helper to resolve segment name from id
      const resolveName = segId => {
        // 1. Try to find in excludedData.segmentsView
        const fromView = excludedData?.segmentsView?.find(v => v.segmentId === segId)
        if (fromView?.segmentName) return fromView.segmentName

        // 2. Fallback to segmentsGroups hook list
        const fromGroups = segmentsGroups?.find(g => g.segmentId === segId)
        if (fromGroups?.segmentName) return fromGroups.segmentName

        // 3. Default to raw id
        return segId
      }

      // Case 1: excludedData is direct array (could be ids or objects)
      if (Array.isArray(excludedData) && excludedData.length > 0) {
        return excludedData
          .map(item => {
            if (typeof item === 'string') return resolveName(item)
            // object may have segmentName or name property
            return item.segmentName || item.name || resolveName(item.segmentId)
          })
          .join(', ')
      }

      // Case 2: we derived names via getSegmentsDatafromSegmentsIds (exludedSegments)
      if (Array.isArray(exludedSegments) && exludedSegments.length > 0) {
        return exludedSegments.map(item => item.name || item.segmentName || item).join(', ')
      }

      // Case 3: API structure {segments: [], segmentsView: []}
      if (excludedData?.segments && Array.isArray(excludedData.segments)) {
        return excludedData.segments.map(segId => resolveName(segId)).join(', ')
      }

      return '-'
    }

    // 1. ALL_CUSTOMERS – display with excluded segments
    if (segmentType === 'ALL_CUSTOMERS') {
      return (
        <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
          <LabelWithVal keyName="All Customers" val={getAllCustomersValue(segmentType)} />
          <LabelWithVal keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>
      )
    }

    // 2. All other segment selections (SELECTED_SEGMENTS or SELECTED_GROUPS)
    const selectedSegmentGroupsRaw = promotionReviewData?.segments?.selectedSegments || []

    // Transform into easy-to-render structure
    const transformedGroups = selectedSegmentGroupsRaw.map((group, idx) => {
      const segmentsView = group?.segmentsView || []
      let segmentNames = []

      if (segmentsView.length > 0) {
        segmentNames = segmentsView.map(seg => seg.segmentName || seg.segmentId)
      } else if (Array.isArray(group?.segments)) {
        // Fallback: resolve names using segmentsGroups hook if we can
        segmentNames = group.segments.map(segId => {
          const match = segmentsGroups?.find(sg => sg.segmentId === segId)
          return match?.segmentName || segId
        })
      }

      // Customer count – try common property names, else undefined
      const customerCount =
        group?.numberOfCustomers || group?.numCustomers || group?.noOfCustomers || group?.customersCount || null

      return {
        id: idx,
        displayGroupId: group?.displayGroupId || String(idx + 1),
        segmentNames,
        customerCount,
      }
    })

    // Compute total customers if counts exist on groups
    const totalCustomers = transformedGroups.every(g => g.customerCount != null)
      ? transformedGroups.reduce((acc, g) => acc + Number(g.customerCount || 0), 0)
      : null

    return (
      <>
        {/* First row – All customers & total */}
        <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
          <LabelWithVal keyName="All Customers" val={getAllCustomersValue()} />
          <LabelWithVal keyName="Total # of Customers" val={totalCustomers != null ? totalCustomers.toString() : '-'} />
        </div>

        {/* Groups grid */}
        {transformedGroups.length > 0 && (
          <div className="mt-4 grid w-full grid-cols-1 gap-6 overflow-hidden md:grid-cols-2 lg:grid-cols-3">
            {transformedGroups.map((group, groupIndex) => (
              <div key={`group-${group.id}`} className="mb-4">
                <div
                  className="mb-2 text-xs font-normal"
                  style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                >
                  Group {group.displayGroupId}
                </div>
                <div
                  className="text-sm font-bold "
                  style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                >
                  {stringifyArray(group.segmentNames)}
                </div>
                {/* --- new: "Group # of Customers" block --- */}
                <div
                  className="mb-2 mt-4 text-xs font-normal"
                  style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                >
                  Group {groupIndex + 1} # of Customers
                </div>
                <div
                  className="text-sm font-bold "
                  style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                >
                  {group.customerCount != null ? group.customerCount.toString() : '-'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Group Customers Count */}
        {transformedGroups.some(g => g.customerCount != null) && (
          <div className="mt-2 grid w-full grid-cols-3 gap-6 overflow-hidden">
            {transformedGroups.map(g => (
              <LabelWithVal
                key={`group-${g.id}-customers`}
                keyName={`Group ${Number(g.displayGroupId)} # of Customers`}
                val={g.customerCount != null ? g.customerCount.toString() : '-'}
              />
            ))}
          </div>
        )}

        {/* Excluded Segments */}
        <div className="mt-2 w-full overflow-hidden pb-5">
          <LabelWithVal keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>

        {/* Info message */}
        {/* <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
          <span className="material-icons-outlined text-gray-400" style={{fontSize: '18px'}}>
            info
          </span>
          <span>Selected segments will not change throughout promotion even if stores within a segment change.</span>
        </div> */}
      </>
    )
  }

  return (
    <AccordionItem
      isDark
      expanded
      size="large"
      className="min-h-12 w-full gap-2 rounded p-4"
      labelClassName="font-body text-base font-medium leading-normal"
      label={
        <div className="flex w-full items-center justify-between">
          <div className="font-medium">Setup</div>
        </div>
      }
      rightAccessoryChild={
        !isDetailView ? (
          <ButtonWithIcon
            iconLeading="edit"
            handleButton={() => handleEditStep(PromotionStep.SETUP)}
            uiSchema={{
              className: 'flex items-center text-sm text-[#0069D7] border-none ml-4',
              title: 'Edit',
              size: SIZE.SMALL,
              variant: COLOR.TERTIARY,
              isDark: true,
            }}
          />
        ) : null
      }
      presentation="test"
      content={
        <div
          className="promotions-content-wrapper w-full overflow-hidden"
          style={{padding: '12px 16px', gap: '16px', minWidth: '1238px'}}
        >
          {/* Promotion Subsection */}
          <div className="promotion-subsection w-full">
            <div className="sc-border heading pb-5 text-base font-bold">Promotion</div>
            <div className="grid w-full grid-cols-1 gap-6 overflow-hidden pb-5">
              <LabelWithVal keyName="Name" val={promotionReviewData?.name} />
            </div>
            <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
              <LabelWithVal keyName="Promotion" val={promotionReviewData?.promotionName || '-'} />
              <LabelWithVal keyName="Promotion Excludes" val={getExcludedItemsDisplay()} />
              <LabelWithVal keyName="Combination of Products" val={getProductCombination()} />
            </div>
          </div>

          {/* Segments Subsection */}
          <div className="segments-subsection mt-6 w-full">
            <div className="sc-border heading pb-5 text-base font-bold">Segments</div>
            {renderSegmentContent()}
          </div>
        </div>
      }
    />
  )
}

export default PromotionsReviewSetup
